import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import { useEffect, useState, useRef } from "react";
import {
  dailySvrTableData,
  selectdailySvrTableData,
} from "../slices/daily-svr-table-data.slice";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Popover,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  Breadcrumbs,
  Link,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import {
  closeMessageModal,
  openMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import {
  SvrEditTempValue,
  FilterCategory,
  DateRangeFilter,
  ColumnOption,
  SummaryRecord,
} from "features/ms6/core/domain/MoneySvr.model";
import { SvrConstantColumnOptions } from "features/ms6/core/money-svr-initial-state";
import {
  selectupdateEditSvr,
  UpdateEditSvrState,
  updateEditSvr,
} from "../slices/update-edit.slice";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const getColorChipQRST = (value: string) => {
  const numValue = parseFloat(value);
  return numValue < 0
    ? { bgcolor: "rgba(211, 47, 47, 0.1)", color: "rgba(211, 47, 47, 1)" }
    : { bgcolor: "rgba(46, 125, 50, 0.1)", color: "rgba(46, 125, 50, 1)" };
};

type SummaryRecordWithId = SummaryRecord & { id: number };

export function FormMoney() {
  const query = useQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const svrUpdateState = useAppSelector(selectupdateEditSvr);

  const [cashDepositInputs, setCashDepositInputs] = useState<
    Record<number, string>
  >({});
  const [shiftInputs, setShiftInputs] = useState<Record<number, string>>({});
  const [cashDepEditableId, setCashDepEditableId] = useState<number | null>(
    null
  );
  const [svrEditTempValue, setSvrTempValue] = useState<SvrEditTempValue>({});

  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [columnAnchorEl, setColumnAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [insightsAnchorEl, setInsightsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const columnSelectButtonRef = useRef<HTMLButtonElement>(null);
  const insightsButtonRef = useRef<HTMLButtonElement>(null);

  const dailySvrTableDataState = useAppSelector(selectdailySvrTableData);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategories, setFilterCategories] = useState<FilterCategory[]>(
    []
  );
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<DateRangeFilter>({
    startDate: "",
    endDate: "",
  });
  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>(
    SvrConstantColumnOptions
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [insightsOptions, setInsightsOptions] = useState([
    { value: "overallSales", label: "Overall Sales", checked: false },
  ]);
  const [openPieChartDialog, setOpenPieChartDialog] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const userStores =
    getAdminSessionState.data?.admin?.user_details?.stores || [];
  const storeIds = userStores.map((store) => store.store_id);

  useEffect(() => {
    console.log("User's authorized stores:", userStores);
  }, [userStores]);

  const toggleFilterDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const toggleColumnDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnAnchorEl(columnAnchorEl ? null : event.currentTarget);
  };

  const toggleInsightsDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    setInsightsAnchorEl(insightsAnchorEl ? null : event.currentTarget);
  };

  useEffect(() => {
    if (dailySvrTableDataState.data?.summary) {
      console.log(
        "Sample summary row:",
        dailySvrTableDataState.data.summary[0]
      );
    }
  }, [dailySvrTableDataState.data]);

  useEffect(() => {
    setSearchTerm("");
    setDateFilter({ startDate: "", endDate: "" });
    initializeFilterCategories();
    setExpandedCategories(["date", "shift", "storeName", "cashier"]);
  }, []);

  useEffect(() => {
    if (svrUpdateState.status === UpdateEditSvrState.success) {
      dispatch(dailySvrTableData());
      setCashDepEditableId(null); // Reset edit mode
      // Update cashDepositInputs with latest data
      setCashDepositInputs((prev) => {
        const updatedInputs = { ...prev };
        displayData.forEach((item) => {
          if (updatedInputs[item.id] && item.cashDeposit) {
            updatedInputs[item.id] = item.cashDeposit;
          }
        });
        return updatedInputs;
      });
    }
  }, [svrUpdateState.status, dispatch]);

  const initializeFilterCategories = () => {
    setFilterCategories([
      {
        name: "Date Range",
        key: "date",
        options: [],
        isDateFilter: true,
      },
      {
        name: "Shift",
        key: "shift",
        options: [
          { value: "AM", label: "AM", checked: false },
          { value: "PM", label: "PM", checked: false },
        ],
      },
      {
        name: "Store Name",
        key: "store_name",
        options: [
          {
            value: "Taters SM MOA Gamepark",
            label: "Taters SM MOA Gamepark",
            checked: false,
          },
        ],
      },
    ]);
  };

  const toggleCategoryExpansion = (categoryKey: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((key) => key !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const handleFilterOptionToggle = (
    categoryIndex: number,
    optionIndex: number
  ) => {
    const updatedCategories = [...filterCategories];
    updatedCategories[categoryIndex].options[optionIndex].checked =
      !updatedCategories[categoryIndex].options[optionIndex].checked;
    setFilterCategories(updatedCategories);
  };

  const handleDateFilterChange = (
    field: keyof DateRangeFilter,
    value: string
  ) => {
    setDateFilter((prev) => ({ ...prev, [field]: value }));
  };

  const handleColumnToggle = (index: number) => {
    if (columnOptions[index].mandatory) return;
    const updatedOptions = [...columnOptions];
    updatedOptions[index].selected = !updatedOptions[index].selected;
    setColumnOptions(updatedOptions);
  };

  const handleInsightsToggle = (index: number) => {
    const updatedOptions = [...insightsOptions];
    updatedOptions[index].checked = !updatedOptions[index].checked;
    setInsightsOptions(updatedOptions);

    if (
      updatedOptions[index].value === "overallSales" &&
      updatedOptions[index].checked
    ) {
      setOpenPieChartDialog(true);
    } else {
      setOpenPieChartDialog(false);
    }
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const getDisplayedColumns = () => {
    return columnOptions.filter(
      (column) => column.mandatory || column.selected
    );
  };

  const isDateInRange = (
    dateStr: string,
    startDate: string,
    endDate: string
  ): boolean => {
    if (!startDate && !endDate) return true;
    const date = new Date(dateStr);
    if (startDate && !endDate) {
      const start = new Date(startDate);
      return date >= start;
    }
    if (!startDate && endDate) {
      const end = new Date(endDate);
      return date <= end;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    return date >= start && date <= end;
  };

  const getFormData = () => {
    let data = dailySvrTableDataState.data?.summary || [];
    const activeFilters = filterCategories.reduce((acc, category) => {
      if (!category.isDateFilter) {
        const selectedOptions = category.options
          .filter((option) => option.checked)
          .map((option) => option.value);
        if (selectedOptions.length > 0) {
          acc[category.key] = selectedOptions;
        }
      }
      return acc;
    }, {} as Record<string, string[]>);

    let filteredData = data.filter((item: any) => {
      if (storeIds.length > 0) {
        const isAuthorizedStore = userStores.some(
          (store) =>
            store.name === item.store_name ||
            String(store.store_id) === String(item.store_name)
        );
        if (!isAuthorizedStore) {
          return false;
        }
      }

      const dateField = "date";
      if (
        (dateFilter.startDate || dateFilter.endDate) &&
        !isDateInRange(
          item[dateField],
          dateFilter.startDate,
          dateFilter.endDate
        )
      ) {
        return false;
      }

      for (const [key, values] of Object.entries(activeFilters)) {
        if (values.length > 0) {
          const itemValue = String(item[key] || "").toLowerCase();
          if (
            !values.some((value) => itemValue.includes(value.toLowerCase()))
          ) {
            return false;
          }
        }
      }

      if (searchTerm && searchTerm.trim() !== "") {
        return Object.entries(item).some(([key, value]) => {
          if (key === "id") return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        });
      }

      return true;
    });

    filteredData.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortDirection === "desc"
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    });

    return filteredData;
  };

  const getActiveFilterCount = () => {
    const standardFilters = filterCategories.reduce((count, category) => {
      if (!category.isDateFilter) {
        return (
          count + category.options.filter((option) => option.checked).length
        );
      }
      return count;
    }, 0);
    const dateFilterActive = dateFilter.startDate || dateFilter.endDate ? 1 : 0;
    return standardFilters + dateFilterActive;
  };

  const filteredData = getFormData() as SummaryRecord[];
  const dataWithIds: SummaryRecordWithId[] = filteredData.map((item, idx) => ({
    ...item,
    id: idx,
  }));

  const maxPage = Math.ceil(dataWithIds.length / rowsPerPage) || 1;
  const displayData = dataWithIds.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleEditClick = (id: number) => {
    if (cashDepEditableId === id) {
      // Exit edit mode and persist shift value
      setCashDepEditableId(null);
    } else {
      // Enter edit mode
      const item = displayData.find((item) => item.id === id);
      setCashDepositInputs((prev) => ({
        ...prev,
        [id]: item?.cashDeposit || "0",
      }));
      setShiftInputs((prev) => ({
        ...prev,
        [id]: item?.shift || "AM", // Default to "AM" if no shift value
      }));
      setCashDepEditableId(id);
    }
  };

  const formatDisplayValue = (
    value: string,
    columnKey: keyof SummaryRecord | "variance_declared_deposited",
    item: SummaryRecordWithId
  ): string => {
    const nonNumericFields: (keyof SummaryRecord)[] = [
      "store_name",
      "date",
      "date_transaction",
      "shift",
      "cashier",
    ];

    if (columnKey === "variance_declared_deposited") {
      const declaredCash = Number(item.declared_cash) || 0;
      const cashDeposit =
        Number(cashDepositInputs[item.id] || item.cashDeposit) || 0;
      const variance = declaredCash - cashDeposit;
      return variance.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    if (nonNumericFields.includes(columnKey as keyof SummaryRecord)) {
      return value;
    }

    if (value === "NaN" || isNaN(parseFloat(value))) {
      return "0";
    }

    return value;
  };

  const getPieChartData = () => {
    const storeSales = filteredData.reduce((acc, item: SummaryRecord) => {
      const storeName = item.store_name;
      const sales = Number(item.overall_sales) || 0;
      acc[storeName] = (acc[storeName] || 0) + sales;
      return acc;
    }, {} as Record<string, number>);

    const labels = Object.keys(storeSales);
    const data = Object.values(storeSales);

    return {
      labels,
      datasets: [
        {
          label: "Overall Sales",
          data,
          backgroundColor: [
            "#FF6384",
            "#36A2EB",
            "#FFCE56",
            "#4BC0C0",
            "#9966FF",
            "#FF9F40",
          ],
          borderColor: [
            "#FF6384",
            "#36A2EB",
            "#FFCE56",
            "#4BC0C0",
            "#9966FF",
            "#FF9F40",
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "#FFFFFF" }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            underline="hover"
            sx={{ display: "flex", alignItems: "center" }}
            color="inherit"
            href="/admin/ms6/dashboard"
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Money Management</Typography>
        </Breadcrumbs>
      </Box>

      <Typography
        variant="h5"
        component="h1"
        sx={{ p: 2, fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem" }}
      >
        DAILY SVR RECORDS
      </Typography>

      <Box sx={{ display: "flex", p: 2, gap: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ flexGrow: 1 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Button
          variant="contained"
          onClick={() => dispatch(dailySvrTableData())}
        >
          Fetch Daily SVR
        </Button>

        <Button
          variant="outlined"
          onClick={toggleInsightsDialog}
          ref={insightsButtonRef}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "120px",
            justifyContent: "space-between",
          }}
        >
          Insights
        </Button>

        <Button
          variant="outlined"
          onClick={toggleInsightsDialog}
          ref={insightsButtonRef}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "120px",
            justifyContent: "space-between",
          }}
        >
          Insights
        </Button>

        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={toggleFilterDialog}
          ref={filterButtonRef}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "120px",
            justifyContent: "space-between",
          }}
        >
          {getActiveFilterCount() === 0
            ? "Filter"
            : `Filters (${getActiveFilterCount()})`}
        </Button>

        <Button
          variant="outlined"
          onClick={toggleColumnDialog}
          ref={columnSelectButtonRef}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "120px",
            justifyContent: "space-between",
          }}
        >
          Columns ({getDisplayedColumns().length})
        </Button>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            displayEmpty
          >
            <MenuItem value={25}>25 rows</MenuItem>
            <MenuItem value={50}>50 rows</MenuItem>
            <MenuItem value={100}>100 rows</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, m: 2 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#222222" }}>
                {getDisplayedColumns().map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      borderBottom: "none",
                      cursor: column.key === "date" ? "pointer" : "default",
                    }}
                    onClick={
                      column.key === "date" ? handleSortToggle : undefined
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {column.label}
                      {column.key === "date" &&
                        (sortDirection === "desc" ? (
                          <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        ))}
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    borderBottom: "none",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.length > 0 ? (
                displayData.map((item: SummaryRecordWithId, index: number) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                  >
                    {getDisplayedColumns().map((column) => (
                      <TableCell key={column.key} sx={{ borderBottom: "none" }}>
                        {column.key === "cashDeposit" &&
                        cashDepEditableId === item.id ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <input
                              type="number"
                              value={cashDepositInputs[item.id] || ""}
                              onChange={(e) =>
                                setCashDepositInputs({
                                  ...cashDepositInputs,
                                  [item.id]: e.target.value,
                                })
                              }
                              style={{
                                width: "100px",
                                padding: "6px 8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "1rem",
                              }}
                            />
                          </Box>
                        ) : column.key === "cashDeposit" ? (
                          <Chip
                            label={Number(
                              cashDepositInputs[item.id] ||
                                item.cashDeposit ||
                                "0"
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            size="small"
                            sx={getColorChipQRST(
                              (
                                cashDepositInputs[item.id] ||
                                item.cashDeposit ||
                                "0"
                              ).toString()
                            )}
                          />
                        ) : column.key === "shift" ? (
                          cashDepEditableId === item.id ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                  value={shiftInputs[item.id] || "AM"}
                                  onChange={(e) =>
                                    setShiftInputs({
                                      ...shiftInputs,
                                      [item.id]: e.target.value as string,
                                    })
                                  }
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  <MenuItem value="AM">AM</MenuItem>
                                  <MenuItem value="PM">PM</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          ) : (
                            <Chip
                              label={shiftInputs[item.id] || item.shift || "AM"}
                              size="small"
                              sx={{
                                bgcolor: "rgba(25, 118, 210, 0.1)",
                                color: "rgba(25, 118, 210, 1)",
                              }}
                            />
                          )
                        ) : column.key === "variance_declared_deposited" ? (
                          <Chip
                            label={formatDisplayValue("", column.key, item)}
                            size="small"
                            sx={getColorChipQRST(
                              formatDisplayValue("", column.key, item)
                            )}
                          />
                        ) : [
                            "cash_over_short",
                            "total_noncash",
                            "total_payments",
                            "overall_sales",
                            "transaction_value",
                          ].includes(column.key) ? (
                          <Chip
                            label={`${
                              Number(item[column.key]) < 0 ? "-" : "+"
                            }${Math.abs(
                              Number(item[column.key])
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                            size="small"
                            sx={getColorChipQRST(
                              (item[column.key] ?? "").toString()
                            )}
                          />
                        ) : typeof item[column.key as keyof SummaryRecord] ===
                          "number" ? (
                          Number(
                            item[column.key as keyof SummaryRecord]
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        ) : (
                          formatDisplayValue(
                            item[column.key] as string,
                            column.key,
                            item
                          )
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditClick(item.id)}
                        sx={{ textTransform: "none" }}
                      >
                        {cashDepEditableId === item.id ? "Done" : "Edit"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={getDisplayedColumns().length + 1}
                    align="center"
                    sx={{ py: 4, borderBottom: "none" }}
                  >
                    {searchTerm || getActiveFilterCount() > 0
                      ? "No matching records found. Try adjusting your filters."
                      : "No records found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {displayData.length} of {filteredData.length} records
          {(searchTerm || getActiveFilterCount() > 0) &&
            ` (filtered from ${
              dailySvrTableDataState.data?.summary?.length || 0
            } total)`}
        </Typography>

        <Pagination
          count={maxPage}
          page={page}
          onChange={handleChangePage}
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { width: 320, maxHeight: 500 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Filter SVR Records
          </Typography>
          {getActiveFilterCount() > 0 && (
            <Typography variant="caption" color="text.secondary">
              {getActiveFilterCount()}{" "}
              {getActiveFilterCount() === 1 ? "filter" : "filters"} applied
            </Typography>
          )}
        </Box>
        <Box sx={{ maxHeight: 350, overflow: "auto" }}>
          {filterCategories.map((category, categoryIndex) => (
            <Box key={categoryIndex} sx={{ borderBottom: "1px solid #f0f0f0" }}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => toggleCategoryExpansion(category.key)}
              >
                <Typography variant="subtitle2">{category.name}</Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    transform: expandedCategories.includes(category.key)
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Box>
              {expandedCategories.includes(category.key) && (
                <Box sx={{ p: 2, pt: 0, pl: 4, bgcolor: "#f5f5f5" }}>
                  {category.isDateFilter ? (
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                        >
                          Start Date
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateFilter.startDate}
                          onChange={(e) =>
                            handleDateFilterChange("startDate", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                        >
                          End Date
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateFilter.endDate}
                          onChange={(e) =>
                            handleDateFilterChange("endDate", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Stack>
                  ) : (
                    category.options.map((option, optionIndex) => (
                      <FormControlLabel
                        key={optionIndex}
                        control={
                          <Checkbox
                            checked={option.checked}
                            onChange={() =>
                              handleFilterOptionToggle(
                                categoryIndex,
                                optionIndex
                              )
                            }
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                        }
                      />
                    ))
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              const resetCategories = filterCategories.map((category) => ({
                ...category,
                options: category.options.map((option) => ({
                  ...option,
                  checked: false,
                })),
              }));
              setFilterCategories(resetCategories);
              setDateFilter({ startDate: "", endDate: "" });
            }}
            color="error"
            size="small"
          >
            Clear all
          </Button>
          <Button
            onClick={() => setFilterAnchorEl(null)}
            variant="contained"
            size="small"
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Columns Popover */}
      <Popover
        open={Boolean(columnAnchorEl)}
        anchorEl={columnAnchorEl}
        onClose={() => setColumnAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { width: 320, maxHeight: 500 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Select Columns to Display
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 350, overflow: "auto" }}>
          <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Typography variant="subtitle2" gutterBottom>
              Mandatory Columns
            </Typography>
            {columnOptions
              .filter((option) => option.mandatory)
              .map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={<Checkbox checked={true} disabled size="small" />}
                  label={
                    <Typography variant="body2">{option.label}</Typography>
                  }
                />
              ))}
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Optional Columns
            </Typography>
            {columnOptions
              .filter((option) => !option.mandatory)
              .map((option, index) => {
                const trueIndex = columnOptions.findIndex(
                  (col) => col.key === option.key
                );
                return (
                  <FormControlLabel
                    key={trueIndex}
                    control={
                      <Checkbox
                        checked={option.selected}
                        onChange={() => handleColumnToggle(trueIndex)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">{option.label}</Typography>
                    }
                  />
                );
              })}
          </Box>
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              const resetColumns = columnOptions.map((col) => ({
                ...col,
                selected: col.mandatory ? true : false,
              }));
              setColumnOptions(resetColumns);
            }}
            color="error"
            size="small"
          >
            Reset
          </Button>
          <Button
            onClick={() => setColumnAnchorEl(null)}
            variant="contained"
            size="small"
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Insights Popover */}
      <Popover
        open={Boolean(insightsAnchorEl)}
        anchorEl={insightsAnchorEl}
        onClose={() => setInsightsAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { width: 320, maxHeight: 500 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Insights
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {insightsOptions.map((option, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={option.checked}
                  onChange={() => handleInsightsToggle(index)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{option.label}</Typography>}
            />
          ))}
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              setInsightsOptions(
                insightsOptions.map((option) => ({
                  ...option,
                  checked: false,
                }))
              );
              setOpenPieChartDialog(false);
            }}
            color="error"
            size="small"
          >
            Clear all
          </Button>
          <Button
            onClick={() => setInsightsAnchorEl(null)}
            variant="contained"
            size="small"
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Pie Chart Dialog */}
      <Dialog
        open={openPieChartDialog}
        onClose={() => {
          setOpenPieChartDialog(false);
          setInsightsOptions(
            insightsOptions.map((option) => ({
              ...option,
              checked: false,
            }))
          );
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Overall Sales Distribution</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400 }}>
            <Pie
              data={getPieChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || "";
                        const value = context.raw as number;
                        return `${label}: ₱${value.toFixed(2)}`;
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
